// @wc-ignore-file

/**
 * CR-162: resolve the project-scoped route of a document from its id alone,
 * so a bare `/docs/:id` link (persisted in old AI messages and emails) can be
 * redirected to `/workspaces/:slug/projects/:projectSlug/docs/:id`.
 *
 * Pure logic, no React: the caller injects how a container row is read (the
 * route wires it to the TanStack Query cache). Every row goes through the
 * caller's RLS-bound client, and any read failure (unknown id, row hidden by
 * RLS, network error) resolves to null — the caller shows not-found.
 */

export interface DocumentRouteContainer {
  id: string;
  container_type: string | null;
  parent_container_id: string | null;
  slug: string | null;
}

export type GetDocumentRouteContainer = (
  id: string,
) => Promise<DocumentRouteContainer | null | undefined>;

export interface DocumentRouteParams {
  slug: string;
  projectSlug: string;
}

/** Ancestor reads allowed above the document (project and workspace included). */
export const MAX_DOCUMENT_ANCESTOR_HOPS = 6;

async function read(
  getContainer: GetDocumentRouteContainer,
  id: string,
): Promise<DocumentRouteContainer | null> {
  try {
    return (await getContainer(id)) ?? null;
  } catch {
    return null;
  }
}

export async function resolveDocumentRouteParams(
  getContainer: GetDocumentRouteContainer,
  documentId: string,
): Promise<DocumentRouteParams | null> {
  const document = await read(getContainer, documentId);
  if (!document || document.container_type !== "document") return null;

  const visited = new Set<string>([document.id]);
  let parentId = document.parent_container_id;
  let hops = 0;
  let projectSlug: string | null = null;

  while (parentId && hops < MAX_DOCUMENT_ANCESTOR_HOPS) {
    if (visited.has(parentId)) return null;
    visited.add(parentId);
    hops += 1;

    const current = await read(getContainer, parentId);
    if (!current) return null;

    if (projectSlug === null) {
      if (current.container_type === "workspace") return null;
      if (current.container_type === "project") {
        if (!current.slug) return null;
        projectSlug = current.slug;
      }
    } else {
      // The row right above the project must be its workspace.
      if (current.container_type !== "workspace" || !current.slug) return null;
      return { slug: current.slug, projectSlug };
    }
    parentId = current.parent_container_id;
  }
  return null;
}
